import React, { useEffect, useState, useMemo, useRef } from 'react';
import { X, ChevronDown, ChevronRight } from 'lucide-react';
import { fetchWellData, type WellDetail as WellDetailType } from '../lib/api';
import { designTokens } from '../styles/design-tokens';
import { useTheme } from '../context/ThemeContext';
import { FilterChip } from './ui/FilterChip';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from 'recharts';

type PeriodOption = '12' | '24' | 'all';

interface WellDetailProps {
  wellId: number;
  flaringMcfDay?: number;
  onClose: () => void;
  savedLocation: import('../lib/api').SavedLocation | undefined;
  onSaveLocation: (input: import('../lib/api').SaveLocationInput) => Promise<import('../lib/api').SavedLocation>;
  onDeleteSavedLocation: (id: number) => Promise<void>;
}

const COLORS = {
  pipeline: '#16a34a',
  plant: '#0891b2',
  leaseUse: '#6b7280',
  flared: '#dc2626',
  undisposed: '#16a34a40',
  gas: '#16a34a',
  oil: '#d97706',
};

export const WellDetail: React.FC<WellDetailProps> = ({
  wellId, flaringMcfDay, onClose,
  savedLocation, onSaveLocation, onDeleteSavedLocation,
}) => {
  const { theme } = useTheme();
  const [well, setWell] = useState<WellDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<PeriodOption>('all');
  const [showTable, setShowTable] = useState(false);
  // Synchronous guard against double-click creating duplicate saved_locations rows.
  const saveToggleInFlightRef = useRef(false);

  useEffect(() => {
    setLoading(true);
    setError(null);
    setWell(null);
    fetchWellData(wellId)
      .then(setWell)
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [wellId]);

  // Compute MW from this well's flared gas
  const wellFlaringMcf = flaringMcfDay ?? computeWellFlaring(well);
  const wellMW = wellFlaringMcf ? (wellFlaringMcf * 1036) / (8500 * 24) : null;

  // Chronological production data (API returns DESC, we reverse)
  const allHistory = useMemo(() => {
    if (!well?.production_history?.length) return [];
    return [...well.production_history].reverse();
  }, [well]);

  // Does this well have disposition data? (TX wells)
  const hasDisposition = useMemo(() =>
    allHistory.some(p =>
      p.gas_pipeline_mcf != null || p.gas_plant_mcf != null || p.gas_lease_use_mcf != null
    ), [allHistory]);

  // Filtered by period toggle
  const filteredHistory = useMemo(() => {
    if (period === 'all') return allHistory;
    const n = period === '12' ? 12 : 24;
    return allHistory.slice(-n);
  }, [allHistory, period]);

  // Chart data
  const chartData = useMemo(() => {
    return filteredHistory.map(p => {
      const totalGas = (p.gas_prod_mcf ?? 0) + (p.csgd_prod_mcf ?? 0);
      const pipeline = p.gas_pipeline_mcf ?? 0;
      const plant = p.gas_plant_mcf ?? 0;
      const leaseUse = p.gas_lease_use_mcf ?? 0;
      const flared = (p.gas_flared_mcf ?? 0) + (p.gas_vented_mcf ?? 0)
        + (p.csgd_flared_mcf ?? 0) + (p.csgd_vented_mcf ?? 0);
      const disposedSum = pipeline + plant + leaseUse + flared;
      const undisposed = hasDisposition ? Math.max(0, totalGas - disposedSum) : 0;

      return {
        period: formatPeriod(p.period),
        totalGas: hasDisposition ? 0 : totalGas,
        pipeline: hasDisposition ? pipeline : 0,
        plant: hasDisposition ? plant : 0,
        leaseUse: hasDisposition ? leaseUse : 0,
        flared: hasDisposition ? flared : 0,
        undisposed,
        oil: Number(p.oil_prod_bbl ?? 0),
      };
    });
  }, [filteredHistory, hasDisposition]);

  // KPI summaries
  const kpis = useMemo(() => {
    let totalGas = 0, totalOil = 0, totalFlared = 0;
    for (const p of filteredHistory) {
      totalGas += (p.gas_prod_mcf ?? 0) + (p.csgd_prod_mcf ?? 0);
      totalOil += Number(p.oil_prod_bbl ?? 0);
      totalFlared += (p.gas_flared_mcf ?? 0) + (p.gas_vented_mcf ?? 0)
        + (p.csgd_flared_mcf ?? 0) + (p.csgd_vented_mcf ?? 0);
    }
    const flaredPct = totalGas > 0 ? (totalFlared / totalGas) * 100 : 0;
    return { totalGas, totalOil, totalFlared, flaredPct };
  }, [filteredHistory]);

  // Disposition totals for summary bar
  const dispositionTotals = useMemo(() => {
    if (!hasDisposition) return null;
    let pipeline = 0, plant = 0, leaseUse = 0, flared = 0;
    for (const p of filteredHistory) {
      pipeline += p.gas_pipeline_mcf ?? 0;
      plant += p.gas_plant_mcf ?? 0;
      leaseUse += p.gas_lease_use_mcf ?? 0;
      flared += (p.gas_flared_mcf ?? 0) + (p.gas_vented_mcf ?? 0)
        + (p.csgd_flared_mcf ?? 0) + (p.csgd_vented_mcf ?? 0);
    }
    const total = pipeline + plant + leaseUse + flared;
    if (total === 0) return null;
    return {
      pipeline: { value: pipeline, pct: (pipeline / total) * 100 },
      plant: { value: plant, pct: (plant / total) * 100 },
      leaseUse: { value: leaseUse, pct: (leaseUse / total) * 100 },
      flared: { value: flared, pct: (flared / total) * 100 },
    };
  }, [filteredHistory, hasDisposition]);

  const hasOil = chartData.some(d => d.oil > 0);

  const thStyle: React.CSSProperties = {
    textAlign: 'left', padding: '4px 4px', color: theme.textTertiary,
    fontWeight: 500, fontSize: '10px',
  };
  const tdStyle: React.CSSProperties = {
    padding: '4px 4px', fontSize: '10px', color: theme.text,
  };

  const customTooltipStyle: React.CSSProperties = {
    background: theme.background,
    border: `1px solid ${theme.border}`,
    borderRadius: '4px',
    padding: '6px 10px',
    fontSize: '11px',
    lineHeight: 1.5,
  };

  return (
    <div style={{
      width: designTokens.layout.detailPanelWidth, background: theme.background,
      borderLeft: `1px solid ${theme.border}`,
      display: 'flex', flexDirection: 'column', overflow: 'hidden', flexShrink: 0,
    }}>
      {/* Header */}
      <div style={{
        padding: '16px', borderBottom: `1px solid ${theme.border}`,
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
      }}>
        <h2 style={{ fontSize: '16px', fontWeight: 700, margin: 0 }}>Well Details</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {well && well.latitude != null && well.longitude != null && (
            <button
              onClick={async (e) => {
                e.stopPropagation();
                if (saveToggleInFlightRef.current) return;
                saveToggleInFlightRef.current = true;
                try {
                  if (savedLocation) {
                    await onDeleteSavedLocation(savedLocation.id);
                  } else {
                    const defaultName =
                      `${well.api_number || `Well ${well.id}`}` +
                      (well.operator ? ` · ${well.operator}` : '');
                    await onSaveLocation({
                      name: defaultName.slice(0, 200),
                      lat: well.latitude!,
                      lng: well.longitude!,
                      source_type: 'well',
                      source_id: String(well.id),
                    });
                  }
                } catch (err) {
                  console.error('[WellDetail] save toggle failed:', err);
                } finally {
                  saveToggleInFlightRef.current = false;
                }
              }}
              title={savedLocation ? 'Remove from saved locations' : 'Save to locations'}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '16px',
                color: savedLocation ? '#eab308' : '#888',
                padding: '2px 6px',
              }}
            >
              {savedLocation ? '★' : '☆'}
            </button>
          )}
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: '2px',
            color: theme.textSecondary,
          }}>
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
        {loading && <p style={{ color: theme.textSecondary, fontSize: '13px' }}>Loading...</p>}
        {error && <p style={{ color: designTokens.colors.error, fontSize: '13px' }}>{error}</p>}

        {well && (
          <>
            {/* Flaring — progress bar */}
            {wellFlaringMcf != null && wellFlaringMcf > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '4px' }}>
                  <span style={{ fontSize: '11px', color: theme.textSecondary }}>Flared Gas (12-mo avg)</span>
                  <span style={{
                    fontFamily: designTokens.typography.fontFamily.mono,
                    fontSize: '13px', fontWeight: 600,
                    color: getFlaringColor(wellFlaringMcf),
                  }}>{wellFlaringMcf.toLocaleString(undefined, { maximumFractionDigits: 1 })} Mcf/d</span>
                </div>
                <div style={{ height: '6px', borderRadius: '3px', background: designTokens.colors.brandFaint, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', borderRadius: '3px',
                    width: `${Math.min(100, (wellFlaringMcf / 2000) * 100)}%`,
                    background: getFlaringColor(wellFlaringMcf),
                  }} />
                </div>
                {wellMW != null && (
                  <div style={{ fontSize: '10px', color: designTokens.colors.mwPotential, fontWeight: 600, marginTop: '3px' }}>
                    ~{wellMW >= 1 ? wellMW.toFixed(1) : (wellMW * 1000).toFixed(0)} {wellMW >= 1 ? 'MW' : 'kW'} potential (Aero SC)
                  </div>
                )}
              </div>
            )}

            {/* Well Identity */}
            <Section title="Well Identity">
              <Field label="API Number" value={well.api_number}
                href={well.api_number && well.state ? getRegulatoryUrl(well.api_number, well.state) : undefined} />
              <Field label="Well Name" value={well.well_name} />
              <Field label="Operator" value={well.operator} />
              <Field label="Type" value={well.well_type} />
              <Field label="Status" value={well.well_status} />
              <Field label="Location" value={`${well.county} County, ${well.state}`} />
            </Section>

            {/* Physical */}
            {(well.depth_ft || well.spud_year || well.latitude_bh || well.lateral_length_ft) && (
              <Section title="Physical">
                <Field label="Total Depth" value={well.depth_ft ? `${well.depth_ft.toLocaleString()} ft` : null} />
                <Field label="Lateral Length" value={well.lateral_length_ft ? `${well.lateral_length_ft.toLocaleString()} ft` : null} />
                <Field label="Spud Year" value={well.spud_year ? String(well.spud_year) : null} />
                {well.latitude_bh && well.longitude_bh && (
                  <Field label="Bottomhole" value={`${well.latitude_bh.toFixed(4)}, ${well.longitude_bh.toFixed(4)}`} />
                )}
              </Section>
            )}

            {/* Production History */}
            {allHistory.length > 0 && (
              <Section title="Production History">
                {/* Period toggle */}
                <div style={{ display: 'flex', gap: '4px', marginBottom: '8px' }}>
                  {(['12', '24', 'all'] as PeriodOption[]).map(opt => (
                    <FilterChip
                      key={opt}
                      label={opt === 'all' ? 'All' : `${opt} mo`}
                      active={period === opt}
                      color={designTokens.colors.primary}
                      onClick={() => setPeriod(opt)}
                    />
                  ))}
                </div>

                {/* KPI row */}
                <div style={{
                  display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap',
                }}>
                  <KPI label="Total Gas" value={fmtGas(kpis.totalGas)} color={COLORS.gas} />
                  {kpis.totalOil > 0 && (
                    <KPI label="Total Oil" value={fmtOil(kpis.totalOil)} color={COLORS.oil} />
                  )}
                  {kpis.flaredPct > 0 && (
                    <KPI label="Flared" value={`${kpis.flaredPct.toFixed(1)}%`} color={COLORS.flared} />
                  )}
                </div>

                {/* Gas production chart */}
                <div style={{ height: 200, marginBottom: hasOil ? '4px' : '8px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 4, right: 4, left: -10, bottom: 0 }}>
                      <XAxis
                        dataKey="period"
                        tick={{ fontSize: 9, fill: theme.textTertiary }}
                        interval={Math.max(0, Math.floor(chartData.length / 6) - 1)}
                        tickLine={false}
                      />
                      <YAxis
                        tick={{ fontSize: 9, fill: theme.textTertiary }}
                        tickFormatter={fmtAxisNum}
                        tickLine={false}
                        axisLine={false}
                        width={40}
                      />
                      <Tooltip content={({ active, payload, label }) => {
                        if (!active || !payload?.length) return null;
                        return (
                          <div style={customTooltipStyle}>
                            <div style={{ fontWeight: 600, marginBottom: '2px' }}>{label}</div>
                            {payload.filter((p: any) => (p.value as number) > 0).map((p: any) => (
                              <div key={p.dataKey} style={{ color: p.fill || p.color }}>
                                {TOOLTIP_LABELS[p.dataKey as string] || p.dataKey}: {Number(p.value).toLocaleString()} Mcf
                              </div>
                            ))}
                          </div>
                        );
                      }} />
                      {hasDisposition ? (
                        <>
                          <Bar dataKey="pipeline" stackId="gas" fill={COLORS.pipeline} />
                          <Bar dataKey="plant" stackId="gas" fill={COLORS.plant} />
                          <Bar dataKey="leaseUse" stackId="gas" fill={COLORS.leaseUse} />
                          <Bar dataKey="flared" stackId="gas" fill={COLORS.flared} />
                          <Bar dataKey="undisposed" stackId="gas" fill={COLORS.undisposed} />
                        </>
                      ) : (
                        <Bar dataKey="totalGas" fill={COLORS.gas} radius={[1, 1, 0, 0]} />
                      )}
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Oil mini-chart */}
                {hasOil && (
                  <div style={{ height: 60, marginBottom: '8px' }}>
                    <div style={{ fontSize: '9px', color: theme.textTertiary, marginBottom: '2px' }}>Oil (bbl)</div>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData} margin={{ top: 0, right: 4, left: -10, bottom: 0 }}>
                        <XAxis dataKey="period" hide />
                        <YAxis
                          tick={{ fontSize: 8, fill: theme.textTertiary }}
                          tickFormatter={fmtAxisNum}
                          tickLine={false}
                          axisLine={false}
                          width={40}
                        />
                        <Tooltip content={({ active, payload, label }) => {
                          if (!active || !payload?.length) return null;
                          const val = payload[0].value as number;
                          if (val === 0) return null;
                          return (
                            <div style={customTooltipStyle}>
                              <div style={{ fontWeight: 600 }}>{label}</div>
                              <div style={{ color: COLORS.oil }}>Oil: {val.toLocaleString()} bbl</div>
                            </div>
                          );
                        }} />
                        <Bar dataKey="oil" fill={COLORS.oil} radius={[1, 1, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Disposition summary bar (TX wells) */}
                {dispositionTotals && (
                  <div style={{ marginBottom: '10px' }}>
                    <div style={{ fontSize: '9px', color: theme.textTertiary, marginBottom: '3px' }}>Gas Disposition</div>
                    <div style={{
                      display: 'flex', height: '10px', borderRadius: '2px', overflow: 'hidden',
                    }}>
                      {dispositionTotals.pipeline.pct > 0 && (
                        <div style={{ width: `${dispositionTotals.pipeline.pct}%`, background: COLORS.pipeline }}
                          title={`Pipeline: ${dispositionTotals.pipeline.pct.toFixed(1)}%`} />
                      )}
                      {dispositionTotals.plant.pct > 0 && (
                        <div style={{ width: `${dispositionTotals.plant.pct}%`, background: COLORS.plant }}
                          title={`Plant: ${dispositionTotals.plant.pct.toFixed(1)}%`} />
                      )}
                      {dispositionTotals.leaseUse.pct > 0 && (
                        <div style={{ width: `${dispositionTotals.leaseUse.pct}%`, background: COLORS.leaseUse }}
                          title={`Lease use: ${dispositionTotals.leaseUse.pct.toFixed(1)}%`} />
                      )}
                      {dispositionTotals.flared.pct > 0 && (
                        <div style={{ width: `${dispositionTotals.flared.pct}%`, background: COLORS.flared }}
                          title={`Flared/vented: ${dispositionTotals.flared.pct.toFixed(1)}%`} />
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', marginTop: '3px', flexWrap: 'wrap' }}>
                      {dispositionTotals.pipeline.pct > 0 && <Legend color={COLORS.pipeline} label="Pipeline" />}
                      {dispositionTotals.plant.pct > 0 && <Legend color={COLORS.plant} label="Plant" />}
                      {dispositionTotals.leaseUse.pct > 0 && <Legend color={COLORS.leaseUse} label="Lease" />}
                      {dispositionTotals.flared.pct > 0 && <Legend color={COLORS.flared} label="Flared" />}
                    </div>
                  </div>
                )}

                {/* Collapsible table */}
                <button
                  onClick={() => setShowTable(!showTable)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '4px',
                    background: 'none', border: 'none', cursor: 'pointer',
                    fontSize: '10px', fontWeight: 600, color: theme.textSecondary,
                    padding: '2px 0', marginBottom: showTable ? '6px' : 0,
                  }}
                >
                  {showTable ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                  {showTable ? 'Hide table' : 'Show table'}
                </button>

                {showTable && (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', fontSize: '10px', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: `1px solid ${theme.borderLight}` }}>
                          <th style={thStyle}>Period</th>
                          <th style={{ ...thStyle, textAlign: 'right' }}>Gas Mcf</th>
                          <th style={{ ...thStyle, textAlign: 'right' }}>Oil Bbl</th>
                          <th style={{ ...thStyle, textAlign: 'right', color: '#dc2626' }}>Flared Mcf</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...filteredHistory].reverse().map((p, i) => {
                          const totalFlared = (p.gas_flared_mcf ?? 0) + (p.gas_vented_mcf ?? 0)
                            + (p.csgd_flared_mcf ?? 0) + (p.csgd_vented_mcf ?? 0);
                          const totalGas = (p.gas_prod_mcf ?? 0) + (p.csgd_prod_mcf ?? 0);
                          return (
                            <tr key={i} style={{ borderBottom: i < filteredHistory.length - 1 ? `1px solid ${theme.borderLight}` : undefined }}>
                              <td style={tdStyle}>{formatPeriod(p.period)}</td>
                              <td style={{ ...tdStyle, textAlign: 'right' }}>{totalGas > 0 ? totalGas.toLocaleString() : '—'}</td>
                              <td style={{ ...tdStyle, textAlign: 'right' }}>{p.oil_prod_bbl ? Number(p.oil_prod_bbl).toLocaleString() : '—'}</td>
                              <td style={{ ...tdStyle, textAlign: 'right', color: totalFlared > 0 ? '#dc2626' : undefined }}>
                                {totalFlared > 0 ? totalFlared.toLocaleString() : '—'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </Section>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const TOOLTIP_LABELS: Record<string, string> = {
  pipeline: 'Pipeline',
  plant: 'Plant',
  leaseUse: 'Lease use',
  flared: 'Flared/vented',
  undisposed: 'Other gas',
  totalGas: 'Gas',
  oil: 'Oil',
};

function fmtGas(mcf: number): string {
  if (mcf >= 1_000_000) return `${(mcf / 1_000_000).toFixed(1)} MMcf`;
  if (mcf >= 1_000) return `${(mcf / 1_000).toFixed(0)} Mcf`;
  return `${Math.round(mcf)} Mcf`;
}

function fmtOil(bbl: number): string {
  if (bbl >= 1_000) return `${(bbl / 1_000).toFixed(1)} Kbbl`;
  return `${Math.round(bbl)} bbl`;
}

function fmtAxisNum(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(0)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return String(n);
}

function getRegulatoryUrl(apiNumber: string, state: string): string | null {
  if (state === 'TX' || state === 'Texas') {
    const digits = apiNumber.replace(/\D/g, '');
    if (digits.length < 6) return null;
    const county = digits.slice(2, 5);
    const unique = digits.slice(5);
    return `https://webapps2.rrc.texas.gov/EWA/wellboreQueryAction.do?searchArgs.apiNoPrefixArg=${county}&searchArgs.apiNoSuffixArg=${unique}&methodToCall=search`;
  }
  if (state === 'NM' || state === 'New Mexico') {
    return `https://wwwapps.emnrd.nm.gov/OCD/OCDPermitting/Data/WellDetails.aspx?api=${encodeURIComponent(apiNumber)}`;
  }
  return null;
}

function computeWellFlaring(well: WellDetailType | null): number | null {
  if (!well?.production_history || well.production_history.length === 0) return null;
  const recent = well.production_history.slice(0, 12);
  const totalFlared = recent.reduce((sum, p) => {
    return sum + (p.gas_flared_mcf ?? 0) + (p.gas_vented_mcf ?? 0)
      + (p.csgd_flared_mcf ?? 0) + (p.csgd_vented_mcf ?? 0);
  }, 0);
  if (totalFlared === 0) return null;
  return Math.round((totalFlared / 365) * 10) / 10;
}

function getFlaringColor(mcfDay: number): string {
  if (mcfDay >= 1000) return '#7a0177';
  if (mcfDay >= 200) return '#c51b8a';
  if (mcfDay >= 50) return '#f768a1';
  return '#fbb4b9';
}

function formatDate(d: string): string {
  try {
    return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
  } catch {
    return d;
  }
}

function formatPeriod(d: string): string {
  try {
    const date = new Date(d);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  } catch {
    return d;
  }
}

const KPI: React.FC<{ label: string; value: string; color: string }> = ({ label, value, color }) => {
  const { theme } = useTheme();
  return (
    <div style={{
      padding: '4px 8px', borderRadius: '4px',
      background: color + '10', border: `1px solid ${color}30`,
    }}>
      <div style={{ fontSize: '13px', fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: '9px', color: theme.textTertiary }}>{label}</div>
    </div>
  );
};

const Legend: React.FC<{ color: string; label: string }> = ({ color, label }) => {
  const { theme } = useTheme();
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
      <div style={{ width: '8px', height: '8px', borderRadius: '1px', background: color }} />
      <span style={{ fontSize: '9px', color: theme.textTertiary }}>{label}</span>
    </div>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => {
  const { theme } = useTheme();
  return (
    <div style={{ marginBottom: '16px' }}>
      <div style={{
        borderLeft: `3px solid ${designTokens.colors.brand}`,
        paddingLeft: '8px',
        fontSize: '10px', fontWeight: 600, color: theme.textSecondary,
        textTransform: 'uppercase', letterSpacing: '0.06em',
        marginBottom: '8px',
      }}>
        {title}
      </div>
      {children}
    </div>
  );
};

const Field: React.FC<{ label: string; value: string | null | undefined; href?: string | null }> = ({ label, value, href }) => {
  const { theme } = useTheme();
  if (!value) return null;
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', padding: '4px 0',
      borderBottom: `1px solid ${theme.borderLight}`,
    }}>
      <span style={{ fontSize: '11px', color: theme.textTertiary }}>{label}</span>
      <span style={{
        fontSize: '12px', fontWeight: 500, color: theme.text,
        fontFamily: designTokens.typography.fontFamily.mono,
      }}>
        {href ? (
          <a href={href} target="_blank" rel="noopener noreferrer"
            style={{ color: theme.link, textDecoration: 'underline' }}>
            {value}
          </a>
        ) : value}
      </span>
    </div>
  );
};
